import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'

import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'

type RoleIndexFloatingProps = {
  placement?: 'fixed' | 'inline'
  className?: string
  showButton?: boolean
  isOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
}

export function RoleIndexFloating({
  placement = 'fixed',
  className,
  showButton = true,
  isOpen: controlledOpen,
  onOpen,
  onClose,
}: RoleIndexFloatingProps) {
  const { t, language } = useI18n()
  const [internalOpen, setInternalOpen] = useState(false)
  const [roleGuideContent, setRoleGuideContent] = useState('')
  const [roleGuideLoading, setRoleGuideLoading] = useState(false)
  const cleanedRoleGuideContent = roleGuideContent.replace(/^# .*(\r?\n)+/, '')
  const isOpen = controlledOpen ?? internalOpen

  const openRoleGuide = () => {
    onOpen?.()
    setInternalOpen(true)
  }

  const closeRoleGuide = () => {
    onClose?.()
    setInternalOpen(false)
  }

  useEffect(() => {
    if (!isOpen)
      return

    const loadRoleGuide = async () => {
      setRoleGuideLoading(true)
      const markdownUrl = `/rules/${language}/ROLE_INDEX.md`
      const response = await fetch(markdownUrl)
      if (response.ok) {
        const content = await response.text()
        setRoleGuideContent(content)
      }
      setRoleGuideLoading(false)
    }

    loadRoleGuide()
  }, [isOpen, language])

  return (
    <>
      {showButton && (
        <Button
          variant="outline"
          size="sm"
          className={cn(
            placement === 'fixed' && 'fixed bottom-6 right-6 z-40 shadow-lg',
            className,
          )}
          onClick={openRoleGuide}
        >
          <span>{t('common.roleGuide.button')}</span>
        </Button>
      )}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-start sm:items-center sm:justify-center">
          <button
            className="absolute inset-0 bg-black/50"
            type="button"
            aria-label={t('common.roleGuide.closeLabel')}
            onClick={closeRoleGuide}
          />
          <div
            className="relative flex h-dvh w-screen flex-col border bg-background shadow-lg sm:h-[80vh] sm:w-[min(960px,95vw)] sm:rounded-lg"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div className="text-base font-semibold">{t('common.roleGuide.title')}</div>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeRoleGuide}
                aria-label={t('common.roleGuide.closeLabel')}
              >
                ✕
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="space-y-6 px-4 py-5 text-sm sm:px-6">
                {roleGuideLoading
                  ? (
                      <div className="py-8 text-center text-muted-foreground">
                        {t('leaderboard.loadingData')}
                      </div>
                    )
                  : (
                      <div className="custom-rules-prose prose prose-slate max-w-none text-left dark:prose-invert prose-h2:text-lg prose-li:my-1 md:prose-h2:text-xl">
                        <ReactMarkdown>{cleanedRoleGuideContent}</ReactMarkdown>
                      </div>
                    )}
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
    </>
  )
}
